import React from 'react';
import { colors, spacing, typography, typographyFlat } from '../../styles/tokens';
import {
  PolicyPriceDebug,
  PolicySignalKey,
  POLICY_SIGNAL_META,
  signalBasisRoles,
} from './PolicySignalsPanel';

export interface IntakePricingData {
  policy_price?: number | null;
  policy_price_debug?: PolicyPriceDebug | null;
  calculated_offer?: number | null;
  offer_ck_floored?: boolean | null;
  price_source?: string | null;
  tcg_export_market?: number | null;
  tcg_low?: number | null;
  manapool_low?: number | null;
  tcgplayer_market?: number | null;
  manapool_market?: number | null;
  ck_buylist?: number | null;
}

interface IntakePricingPanelProps {
  data: IntakePricingData;
  /** Compact strip for batch review rows */
  compact?: boolean;
}

type RowKind = 'headline' | 'meta' | 'signal' | 'finish' | 'policy';

interface PricingRow {
  label: string;
  value: string;
  kind: RowKind;
  signalKey?: PolicySignalKey;
  emphasize?: boolean;
}

function money(value?: number | null): string {
  return value != null && Number.isFinite(Number(value)) ? `$${Number(value).toFixed(2)}` : '—';
}

function asMoney(value?: number | null): number | null {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Math.round(Number(value) * 100) / 100;
}

function sameMoney(a?: number | null, b?: number | null): boolean {
  const left = asMoney(a);
  const right = asMoney(b);
  if (left == null && right == null) return true;
  return left != null && right != null && left === right;
}

function ageLabel(age?: number | null): string {
  if (age == null || !Number.isFinite(Number(age))) {
    return 'new (100% high)';
  }
  return `${Math.round(Number(age))}d`;
}

const SIGNAL_STATUS_LABELS: Record<string, string> = {
  ok: '',
  stale: 'stale — refresh export/intel',
  missing_ids: 'no TCG id / SKU',
  no_export_row: 'not in TCG export',
  bad_condition: 'unsupported condition',
  no_catalog: 'not in Manapool catalog',
  null_low: 'catalog low empty',
  variant_stamp: '',
  not_eligible: 'n/a (non-MTG)',
};

function unavailableLabel(
  status?: string | null,
  staleValue?: number | null,
): string {
  if (status === 'stale') {
    const staleMoney = money(staleValue);
    return staleMoney !== '—'
      ? `stale ${staleMoney}`
      : SIGNAL_STATUS_LABELS.stale;
  }
  if (status && SIGNAL_STATUS_LABELS[status]) {
    return SIGNAL_STATUS_LABELS[status];
  }
  if (status) return status.replace(/_/g, ' ');
  return 'unavailable';
}

function signalValue(
  live?: number | null,
  status?: string | null,
  stale?: number | null,
): string {
  const liveMoney = money(live);
  if (liveMoney !== '—') return liveMoney;
  return unavailableLabel(status, stale);
}

/** Build deduplicated pricing rows for intake preview / batch review. */
export function buildIntakePricingRows(data: IntakePricingData): PricingRow[] {
  const debug = data.policy_price_debug || {};
  const roles = signalBasisRoles(debug);
  const isTcgcsv = (data.price_source || '').toLowerCase() === 'tcgcsv';

  const tcgExportM = data.tcg_export_market ?? debug.tcg_market;
  const tcgExportL = data.tcg_low ?? debug.tcg_low;
  const manaLow = data.manapool_low ?? debug.manapool_low;
  const manaMarket = debug.manapool_market;
  const catalogTcg = data.tcgplayer_market;
  const catalogMana = data.manapool_market;
  const tcgStatus = debug.tcg_export_status;
  const manaLowStatus = debug.manapool_low_status;

  const rows: PricingRow[] = [
    {
      label: 'Policy price',
      value: money(data.policy_price ?? debug.policy_price),
      kind: 'headline',
      emphasize: true,
    },
    {
      label: 'Calculated offer',
      value: money(data.calculated_offer),
      kind: 'headline',
      emphasize: true,
    },
  ];

  if (data.offer_ck_floored) {
    rows.push({
      label: 'Offer floor',
      value: 'CK buylist',
      kind: 'meta',
    });
  }

  if (data.price_source) {
    rows.push({
      label: 'Price source',
      value: data.price_source,
      kind: 'meta',
    });
  }

  if (!isTcgcsv) {
    rows.push(
      {
        label: 'TCG export (M)',
        value: signalValue(tcgExportM, tcgStatus, debug.tcg_export_stale_market),
        kind: 'signal',
        signalKey: 'tcg_market',
      },
      {
        label: 'TCG export (L)',
        value: signalValue(tcgExportL, tcgStatus, debug.tcg_export_stale_low),
        kind: 'signal',
        signalKey: 'tcg_low',
      },
      {
        label: 'Manapool low',
        value: signalValue(manaLow, manaLowStatus, debug.manapool_low_stale),
        kind: 'signal',
        signalKey: 'manapool_low',
      },
      {
        label: 'Manapool market',
        value: money(manaMarket),
        kind: 'signal',
        signalKey: 'manapool_market',
      },
    );

    if (!sameMoney(catalogTcg, tcgExportM) && catalogTcg != null) {
      rows.push({
        label: 'MTGJSON TCG (finish)',
        value: money(catalogTcg),
        kind: 'finish',
      });
    }

    if (!sameMoney(catalogMana, manaMarket) && catalogMana != null) {
      rows.push({
        label: 'MTGJSON Mana (finish)',
        value: money(catalogMana),
        kind: 'finish',
      });
    }

    if (data.ck_buylist != null) {
      rows.push({
        label: 'CK Buylist',
        value: money(data.ck_buylist),
        kind: 'finish',
      });
    }
  } else if (catalogTcg != null) {
    rows.push({
      label: 'TCGCSV market (finish)',
      value: money(catalogTcg),
      kind: 'finish',
    });
  }

  const highText = money(debug.high);
  const lowText =
    debug.low != null
      ? money(debug.low)
      : highText !== '—'
        ? 'no low comps'
        : 'unavailable';
  rows.push(
    {
      label: 'High / Low',
      value: `${highText} / ${lowText}${
        debug.outlier_rejected != null ? ` · dropped ${money(debug.outlier_rejected)}` : ''
      }`,
      kind: 'policy',
    },
    {
      label: 'Age / blend',
      value: `${ageLabel(debug.age_days)}${debug.blend ? ` · ${debug.blend}` : ''}`,
      kind: 'policy',
    },
  );

  return rows.map((row) => {
    if (!row.signalKey) return row;
    const role =
      roles.highKeys.includes(row.signalKey)
        ? 'high'
        : roles.lowKeys.includes(row.signalKey)
          ? 'low'
          : null;
    if (role === 'high') {
      return { ...row, label: `${row.label} (basis)` };
    }
    if (role === 'low') {
      return { ...row, label: `${row.label} (low)` };
    }
    return row;
  });
}

function signalRoleLabel(key: PolicySignalKey, roles: ReturnType<typeof signalBasisRoles>): string {
  if (roles.highKeys.includes(key)) return ' ★';
  if (roles.lowKeys.includes(key)) return ' ↓';
  return '';
}

export function IntakePricingPanel({ data, compact = false }: IntakePricingPanelProps) {
  const debug = data.policy_price_debug || {};
  const roles = signalBasisRoles(debug);
  const isTcgcsv = (data.price_source || '').toLowerCase() === 'tcgcsv';

  if (compact) {
    return (
      <div style={styles.compactWrap}>
        <div style={styles.compactHeadline}>
          <span>Policy {money(data.policy_price ?? debug.policy_price)}</span>
          <span>Offer {money(data.calculated_offer)}</span>
          {data.offer_ck_floored ? <span>CK floor</span> : null}
          {data.price_source ? <span>{data.price_source}</span> : null}
        </div>
        <div style={styles.compactSignals}>
          {isTcgcsv ? (
            <span
              style={{
                ...styles.compactChip,
                ...(data.tcgplayer_market == null ? styles.compactChipEmpty : {}),
              }}
            >
              <span style={styles.compactKey}>TCGCSV</span>
              {money(data.tcgplayer_market)}
            </span>
          ) : (
            POLICY_SIGNAL_META.map(({ key, short }) => {
              const value =
                key === 'tcg_market'
                  ? (data.tcg_export_market ?? debug.tcg_market)
                  : key === 'tcg_low'
                    ? (data.tcg_low ?? debug.tcg_low)
                    : key === 'manapool_low'
                      ? (data.manapool_low ?? debug.manapool_low)
                      : debug[key];
              const status =
                key === 'tcg_market' || key === 'tcg_low'
                  ? debug.tcg_export_status
                  : key === 'manapool_low'
                    ? debug.manapool_low_status
                    : null;
              const stale =
                key === 'tcg_market'
                  ? debug.tcg_export_stale_market
                  : key === 'tcg_low'
                    ? debug.tcg_export_stale_low
                    : key === 'manapool_low'
                      ? debug.manapool_low_stale
                      : null;
              const display = signalValue(value, status, stale);
              const role = signalRoleLabel(key, roles);
              return (
                <span
                  key={key}
                  style={{
                    ...styles.compactChip,
                    ...(roles.highKeys.includes(key) ? styles.compactChipHigh : {}),
                    ...(roles.lowKeys.includes(key) ? styles.compactChipLow : {}),
                    ...(value == null ? styles.compactChipEmpty : {}),
                  }}
                >
                  <span style={styles.compactKey}>{short}</span>
                  {display}
                  {role}
                </span>
              );
            })
          )}
        </div>
      </div>
    );
  }

  const rows = buildIntakePricingRows(data);

  return (
    <div style={styles.wrap}>
      <div style={styles.grid}>
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <span
              style={{
                ...styles.label,
                ...(row.kind === 'headline' ? styles.labelHeadline : {}),
                ...(row.signalKey && roles.highKeys.includes(row.signalKey) ? styles.labelHigh : {}),
                ...(row.signalKey && roles.lowKeys.includes(row.signalKey) ? styles.labelLow : {}),
              }}
            >
              {row.label}
            </span>
            <span
              style={{
                ...styles.value,
                ...(row.emphasize ? styles.valueHeadline : {}),
                ...(row.signalKey && roles.highKeys.includes(row.signalKey) ? styles.valueHigh : {}),
                ...(row.signalKey && roles.lowKeys.includes(row.signalKey) ? styles.valueLow : {}),
              }}
            >
              {row.value}
            </span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.sm,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: spacing.md,
    rowGap: spacing.xs,
  },
  label: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  labelHeadline: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.text,
  },
  labelHigh: {
    color: colors.primaryDark,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  labelLow: {
    color: colors.secondaryDark,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  value: {
    fontSize: typography.small.fontSize,
    color: colors.text,
    textAlign: 'right' as const,
  },
  valueHeadline: {
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.primary,
  },
  valueHigh: {
    color: colors.primaryDark,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  valueLow: {
    color: colors.secondaryDark,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  compactWrap: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
    marginTop: spacing.xs,
  },
  compactHeadline: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing.sm,
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
  },
  compactSignals: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '4px',
  },
  compactChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '1px 6px',
    borderRadius: '4px',
    fontSize: typographyFlat.fontSize.xs,
    backgroundColor: colors.gray100,
    color: colors.textSecondary,
    border: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap' as const,
  },
  compactChipHigh: {
    backgroundColor: `${colors.primary}18`,
    color: colors.primaryDark,
    borderColor: colors.primaryLight,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  compactChipLow: {
    backgroundColor: `${colors.secondary}18`,
    color: colors.secondaryDark,
    borderColor: colors.secondaryLight,
    fontWeight: typographyFlat.fontWeight.medium,
  },
  compactChipEmpty: {
    opacity: 0.55,
  },
  compactKey: {
    fontWeight: typographyFlat.fontWeight.medium,
  },
};
